import type { Provider, FetchResult } from './types'

function normalizeWikiPage(input: string) {
  const trimmed = (input || '').replace(/^\/+|\/+$/g, '')
  const noMd = trimmed.replace(/\.md$/i, '')
  return noMd.replace(/\s+/g, '-')
}

export function githubWikiProvider(owner: string, repo: string, sourceId: string): Provider {
  const RAW_BASE = `https://raw.githubusercontent.com/wiki/${owner}/${repo}`
  const WEB_BASE = `https://github.com/${owner}/${repo}/wiki`

  async function fetchDoc(slug: string): Promise<FetchResult> {
    const page = normalizeWikiPage(slug)
    const file = `${encodeURIComponent(page)}.md`
    const fetchUrl = `${RAW_BASE}/${file}`
    const res = await fetch(fetchUrl, { cache: 'no-cache' })
    if (!res.ok) throw new Error(`Wiki page not found: ${page}`)
    const md = await res.text()
    // For Home, GitHub serves the wiki root without "/Home"; otherwise link to the specific page.
    const viewUrl = page.toLowerCase() === 'home' ? `${WEB_BASE}` : `${WEB_BASE}/${encodeURIComponent(page)}`
    return { md, viewUrl }
  }

  async function fetchSidebar(): Promise<string | null> {
    const candidates = [`${RAW_BASE}/_Sidebar.md`, `${RAW_BASE}/Home.md`]
    for (const url of candidates) {
      try {
        const r = await fetch(url, { cache: 'no-cache' })
        if (!r.ok) continue
        return await r.text()
      } catch (_e) {
        // try next
      }
    }
    return null
  }

  function rewriteLink(href: string): { internalTo?: string; externalTo?: string } {
    const h = String(href || '')
    if (!h) return {}
    const isAbsolute = /^(https?:)?\/\//i.test(h)
    const isHash = h.startsWith('#') || h.startsWith('mailto:')
    if (isHash) return { externalTo: h }
    if (isAbsolute) {
      try {
        const u = new URL(h)
        const host = u.host.toLowerCase()
        const path = u.pathname
        const webMatch = new RegExp(`^\/${owner}\/${repo}\/wiki\/(.+)$`).exec(path)
        if ((host === 'github.com' || host === 'www.github.com') && webMatch) {
          const page = decodeURIComponent(webMatch[1])
          const slug = normalizeWikiPage(page).replace(/\.md$/i, '')
          const hash = u.hash || ''
          return { internalTo: `/docs/${sourceId}/${slug}${hash}` }
        }
        const rawMatch = new RegExp(`^\/wiki\/${owner}\/${repo}\/(.+)\.md$`).exec(path)
        // noinspection SpellCheckingInspection
        if (host === 'raw.githubusercontent.com' && rawMatch) {
          const page = decodeURIComponent(rawMatch[1])
          const slug = normalizeWikiPage(page)
          const hash = u.hash || ''
          return { internalTo: `/docs/${sourceId}/${slug}${hash}` }
        }
      } catch (_e) {
        // ignore
      }
      return { externalTo: h }
    }
    const normalized = normalizeWikiPage(h).replace(/\.md$/i, '')
    return { internalTo: `/docs/${sourceId}/${normalized}` }
  }

  return {
    fetchDoc,
    fetchSidebar,
    rewriteLink,
    normalizeSlug: normalizeWikiPage,
    defaultPage: 'Home',
  }
}
